import {
  Job,
  GetProductCatalogProvidersDocument,
  GetJobThreeDFilesDocument,
  GetInitialModelTexturesDocument,
  CreateHiDefRenderingDocument,
  GetJobRenderingsDocument,
} from 'src/generated/graphql';
import gqlClient from 'src/apolloClient';
import { DocumentNode } from 'graphql';
import utils from 'src/utils';
import { OperationVariables } from '@apollo/react-common';

const {
  convertManufacturersResponseData,
  getProviderIdsFromJob,
  convertTexturesResponseData,
} = utils.api;

export default class GraphQLApi {
  static async createHiDefRendering(jobId: number) {
    const response = await gqlClient.mutate({
      mutation: CreateHiDefRenderingDocument,
      variables: { jobId },
    });

    const { requestFormUrl } = response.data?.createHiDefRendering;

    return requestFormUrl;
  }

  static async getThreeDDataForJob(jobId: number) {
    const filesResponse = await gqlClient.query({
      query: GetJobThreeDFilesDocument,
      variables: { id: jobId },
    });

    const {
      metadataUrl,
      geometryUrl,
    } = filesResponse.data.jobs.results[0].threeDFiles;

    const [metadata, geometry] = await Promise.all(
      [metadataUrl, geometryUrl].map(async (url) => {
        const response = await fetch(url);
        const data = await response.json();
        return data;
      }),
    );

    return {
      metadata,
      geometry,
    };
  }

  static async getTextures(legacyIds: [number?] = []) {
    const data = await this.fetchAllResults(
      GetInitialModelTexturesDocument,
      { legacyIds },
      'productCatalogTextures',
    );

    return {
      data: convertTexturesResponseData(data),
    };
  }

  static async getRenderings(id: number) {
    const res = await gqlClient.query({
      query: GetJobRenderingsDocument,
      variables: { id },
    });

    return res.data?.jobs?.results[0]?.hiDefRenderings;
  }

  static async getManufacturers(job: Job) {
    const variables: OperationVariables = {};
    const legacyIds = getProviderIdsFromJob(job);

    if (legacyIds.length) {
      variables.legacyIds = legacyIds;
    }

    const data = await this.fetchAllResults(
      GetProductCatalogProvidersDocument,
      variables,
      'productCatalogProviders',
    );

    return {
      data: convertManufacturersResponseData(data),
    };
  }

  private static async fetchAllResults(
    query: DocumentNode,
    variables: OperationVariables,
    key: string,
  ) {
    const results = [];
    const iterator = this.getResultIterator(query, variables, key);
    for await (const page of iterator) {
      results.push(...page);
    }

    return results;
  }

  private static async *getResultIterator(
    query: DocumentNode,
    variables: OperationVariables,
    key: string,
  ) {
    let response = await gqlClient.query({
      query,
      variables,
    });

    yield response.data[key].nodes;

    let hasNext = response.data[key].pageInfo.hasNextPage;

    while (hasNext) {
      const { endCursor } = response.data[key].pageInfo;
      // eslint-disable-next-line no-await-in-loop
      response = await gqlClient.query({
        query,
        variables: {
          ...variables,
          after: endCursor,
        },
      });

      yield response.data[key].nodes;
      hasNext = response.data[key].pageInfo.hasNextPage;
    }
  }
}
